var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g = Object.create((typeof Iterator === "function" ? Iterator : Object).prototype);
    return g.next = verb(0), g["throw"] = verb(1), g["return"] = verb(2), typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (g && (g = 0, op[0] && (_ = 0)), _) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
/**
 * CZNA-456 — hook for `GET /czna-authorities/module-settings/form-configs`.
 *
 * Lists the named form-config templates, optionally narrowed to one authority
 * type. Two callers with different needs:
 *
 * - **Settings** passes a `recordType` — it edits one type at a time and needs
 *   that type's templates plus their `userCreated` flag (packaged rows offer
 *   "reset to default", NK's own offer "delete").
 * - **The create dialog** passes nothing and gets every type in one request,
 *   because it has to know, for each of the nine types on the grid, whether a
 *   template step is needed at all. Nine per-type requests to answer one
 *   yes/no per tile would be the wrong trade.
 *
 * MEMORY rule: `useOkapiKy()` returns an unstable reference. The instance is
 * captured in the query-function closure and NEVER listed in a dep array —
 * listing it is an infinite refetch loop.
 */
import { useOkapiKy } from '@folio/stripes/core';
import { useQuery } from 'react-query';
import { API_PATHS } from '../types/apiPaths';
import { DEFAULT_FORM_CONFIG_TEMPLATE_ID, } from '../types/api';
/**
 * Stable query key. `undefined` (all types) and a specific type are distinct
 * cache slots on purpose — the unfiltered list is a superset, but teaching
 * react-query to derive one from the other would mean maintaining a select
 * function that silently returns a stale subset after a partial invalidation.
 */
export var formConfigTemplatesQueryKey = function (recordType) {
    return ['form-config-templates', recordType !== null && recordType !== void 0 ? recordType : 'ALL'];
};
/**
 * Order templates for display: the packaged `default` first (it is the
 * complete form and the copy source), then the rest alphabetically by id so
 * the list does not reshuffle as NK adds templates.
 */
export var sortTemplates = function (templates) { return __spreadArray([], templates, true).sort(function (a, b) {
    if (a.templateId === DEFAULT_FORM_CONFIG_TEMPLATE_ID)
        return -1;
    if (b.templateId === DEFAULT_FORM_CONFIG_TEMPLATE_ID)
        return 1;
    return a.templateId.localeCompare(b.templateId);
}); };
/**
 * Human label for one template: the `templateLabel` in the active locale,
 * falling back to Czech, then to any present translation, then to
 * `defaultName` for the packaged `default` template, then to the id.
 *
 * The fallback chain matters because `templateLabel` is optional in the
 * contract and null on packaged templates today — rendering an empty string
 * would produce an unselectable blank row in the picker.
 *
 * CZNA-452 — `defaultName`. Every packaged form-config ships WITHOUT a
 * `templateLabel`, so the id-fallback was what NK actually saw: the settings
 * selector read "default (výchozí)" and the create-time chooser offered
 * "default" next to "Nakladatelé". A cataloguer picking between the two is
 * being shown a technical id where a name belongs.
 *
 * The fix is a display fallback rather than a `templateLabel` in the packaged
 * JSONs on purpose. A packaged row that NK has already edited carries
 * `customized: true`, and reconcile then keeps its stored value forever (see
 * `ModuleSettingDefaultsLoader`) — a label added to the JSON would reach some
 * tenants' rows and silently skip others, which is worse than not fixing it.
 * A fallback applies everywhere, and the moment NK renames the template the
 * stored `templateLabel` wins over it, which is the right precedence.
 *
 * @param defaultName what to call the packaged complete form when it has no
 *   stored label. Callers pass the localised string; omitting it keeps the old
 *   id-fallback behaviour, which is what a non-`default` unlabelled template
 *   (only reachable if a create request omitted the label) should still get.
 */
export var templateDisplayName = function (template, locale, defaultName) {
    var _a, _b, _c;
    var labels = template.templateLabel;
    if (labels) {
        // `locale` is typed as required by react-intl but is genuinely absent in
        // some harnesses, and a throw here would take down the whole picker for the
        // sake of a display string. Degrade to the cs/en/any chain instead.
        var short = locale ? locale.split('-')[0] : '';
        var hit = (_c = (_b = (_a = (locale ? labels[locale] : undefined)) !== null && _a !== void 0 ? _a : labels[short]) !== null && _b !== void 0 ? _b : labels.cs) !== null && _c !== void 0 ? _c : labels.en;
        if (hit)
            return hit;
        var first = Object.values(labels).find(function (v) { return Boolean(v); });
        if (first)
            return first;
    }
    if (defaultName && template.templateId === DEFAULT_FORM_CONFIG_TEMPLATE_ID) {
        return defaultName;
    }
    return template.templateId;
};
var useFormConfigTemplates = function (recordType) {
    var _a;
    // Closure-capture only. MEMORY: ky is unstable — never in deps.
    var ky = useOkapiKy();
    var _b = useQuery(formConfigTemplatesQueryKey(recordType), function () { return __awaiter(void 0, void 0, void 0, function () {
        var search;
        return __generator(this, function (_a) {
            search = recordType
                ? "?type=".concat(encodeURIComponent(recordType))
                : '';
            return [2 /*return*/, ky
                    .get("".concat(API_PATHS.CZNA_MODULE_SETTINGS_FORM_CONFIGS).concat(search))
                    .json()];
        });
    }); }, {
        staleTime: 0,
        refetchOnWindowFocus: true,
        retry: false,
    }), data = _b.data, isLoading = _b.isLoading, isError = _b.isError, refetch = _b.refetch;
    return {
        templates: sortTemplates((_a = data === null || data === void 0 ? void 0 : data.templates) !== null && _a !== void 0 ? _a : []),
        isLoading: isLoading,
        isError: isError,
        // Deliberately drops the promise: callers use this as a fire-and-forget
        // "reload the list", and the query's own isError/isLoading already carry
        // the outcome.
        refetch: function () { refetch(); },
    };
};
export default useFormConfigTemplates;
